/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Ganturin Vladimir <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {CollectionViewer, DataSource} from '@angular/cdk/collections';
import {LazyCatalog} from 'compob2b-libs';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CustomerCatalogService} from '../services/customer-catalog.service';

/**
 * Lazy data source для плоского mat-tree. Хранит плоский массив узлов,
 * вставляя/удаляя потомков по требованию (toggleNode вызывается из компонента
 * через (expandedChange) на mat-tree-node).
 */
export class CustomerDynamicDataSource implements DataSource<LazyCatalog> {

  dataChange = new BehaviorSubject<LazyCatalog[]>([]);

  // Узлы, которые пользователь свернул в момент, пока HTTP за их детьми ещё
  // в полёте. Когда ответ приходит — проверяем флаг и НЕ вставляем детей,
  // чтобы не получить «призрачных» детей в свёрнутом узле.
  private cancelledExpansions = new Set<number>();

  get data(): LazyCatalog[] {
    return this.dataChange.value;
  }

  set data(value: LazyCatalog[]) {
    this.dataChange.next(value);
  }

  constructor(private readonly catalogService: CustomerCatalogService,
              private readonly unsubscribeFlag: Subject<boolean>
  ) {
  }

  connect(collectionViewer: CollectionViewer): Observable<LazyCatalog[]> {
    return this.dataChange;
  }

  disconnect(collectionViewer: CollectionViewer): void {
  }

  /**
   * Раскрытие/сворачивание узла: на expand тянем детей с бэка и вставляем
   * в плоский массив сразу за узлом, на collapse — синхронно вырезаем потомков.
   */
  toggleNode(node: LazyCatalog, expand: boolean) {
    const index = this.data.indexOf(node);
    if (index < 0) {
      return;
    }

    // Collapse — чисто локальная операция, HTTP не нужен. Идём вперёд по плоскому
    // массиву, пока level потомков больше уровня узла, и вырезаем эту полосу.
    // Если для этого узла есть in-flight expand — помечаем как отменённый,
    // чтобы пришедшие дети не вставились в свёрнутый узел.
    if (!expand) {
      if (node.isLoading) {
        this.cancelledExpansions.add(node.id);
      }
      let end = index + 1;
      while (end < this.data.length && this.data[end].level > node.level) {
        end++;
      }
      const count = end - index - 1;
      if (count > 0) {
        this.data.splice(index + 1, count);
        this.dataChange.next(this.data);
      }
      return;
    }

    // Expand: пользователь хочет раскрыть. Снимаем флаг отмены (если был
    // от предыдущего collapse'а), чтобы прилетевший ответ применился.
    this.cancelledExpansions.delete(node.id);

    // Дедуп: если уже идёт fetch — не стартуем второй. Текущий резолвится
    // и вставит детей; повторный клик безвреден.
    if (node.isLoading) {
      return;
    }

    node.isLoading = true;
    this.catalogService.getLazyChildren(node.id)
      .pipe(takeUntil(this.unsubscribeFlag))
      .subscribe({
        next: children => {
          node.isLoading = false;
          // Пользователь успел свернуть, пока мы ждали ответ — игнорим.
          if (this.cancelledExpansions.delete(node.id)) {
            return;
          }
          // Индекс мог сдвинуться, пока ждали ответ (соседние узлы раскрылись/свернулись).
          const freshIndex = this.data.indexOf(node);
          if (children?.length && freshIndex >= 0) {
            this.data.splice(freshIndex + 1, 0, ...children);
            this.dataChange.next(this.data);
          }
        },
        error: () => {
          node.isLoading = false;
          this.cancelledExpansions.delete(node.id);
          console.warn('Failed to load catalog children for', node.id);
        },
      });
  }
}
